import { useNavigate, useParams } from 'react-router-dom';
import { Flex, Text, Paper, Badge, Accordion, ThemeIcon, Loader, Select } from '@mantine/core';
import { DESC } from '../../data/trainingMock';
import { IconArrowLeft, IconCircleCheck, IconExternalLink, IconCircleX, IconArrowUp, IconArrowDown, IconBarbell, IconClock, IconGauge, IconWeight, IconBike, IconHeartbeat, IconTransfer, IconRun, IconReload, IconClockUp, IconTreadmill } from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import colors from '../../utils/colors';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { fadeIn,fadeInFromTop, fadeInFromBottom, staggerContainer } from '../../utils/animations';
import { useLoadingOverlay } from '../../components/LoadingOverlayContext';
import { useTrainingSessions } from '../../hooks/useTrainingSessions';
import { useSession } from '../../hooks/useSession';
import { useTrainings } from '../../hooks/useTrainings';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { logout } from '../../utils/auth';

const gradBtn =
  'uppercase text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:from-custom36s hover:to-custom36p ' +
  'focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-bold rounded-lg text-md px-6 py-2 flex items-center justify-center w-full';
// const smallGradBtn =
//   'uppercase text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:from-custom36s hover:to-custom36p ' +
//   'focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-bold rounded-lg text-xs px-3 py-1 flex items-center justify-center';
// const backBtn =
//   'uppercase text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:bg-custom36s font-bold rounded-lg text-xs px-2.5 py-0.5 flex items-center justify-center';

const Sparkle = ({ style, color = '#FFF' }: { style?: React.CSSProperties, color?: string }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    animate={{ 
      scale: [0, 1, 0], 
      opacity: [0, 1, 0],
      rotate: [-45, 45] 
    }}
    transition={{ duration: 0.8, repeat: 1, repeatDelay: 0.2 }}
    style={{
      position: 'absolute',
      backgroundColor: color,
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      boxShadow: `0 0 8px 2px ${color}80`,
      ...style
    }}
  />
);

const markExerciseAsDone = async (
  userTrainingId: number,
  trainingStepId: number,
  exerciseId: number,
  typedExerciseId: number,
  exerciseType: string,
  params: Record<string, any>
) => {
  const apiKey = localStorage.getItem('apiKey');

  let exerciseParams: Record<string, any> = {};
  
  if (exerciseType === 'MACHINE') {
    exerciseParams.machineType = {
      concentric: params.concentric || 0,
      eccentric: params.eccentric || 0,
      delta: params.delta || 0,
      rate: params.rate || 0,
      reps: params.reps || 0,
    };
  } else if (exerciseType === 'MACHINE_POWER_BASED') {
    exerciseParams.machinePowerBasedType = {
      brakeW: params.brakeW || 0,
      brakeNm: params.brakeNm || 0,
      timer: params.timer || 0,
      rate: params.rate || 0,
    };
  } else if (exerciseType === 'CARDIO') {
    exerciseParams.cardioType = {
      km: params.kilometry || 0,
      pulse: params.puls || 0,
      rate: params.rate || 0,
      reps: params.reps || 0
    };
  } else if (exerciseType === 'STRENGTH') {
    exerciseParams.strengthType = {
      reps: params.reps || 0
    };
  } else if (exerciseType === 'OTHER') {
    exerciseParams.otherType = {
      pulse: params.puls || 0,
      rate: params.rate || 0,
      reps: params.reps || 0
    };
  } else if (exerciseType === 'CONSULTATION') {
    exerciseParams.consultationType = {};
  }

  const payload = {
    userTrainingId,
    trainingStepId,
    exerciseId,
    typedExerciseId,
    exerciseParams
  };

  //console.log('Sending exercise done payload:', payload);

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_STUDIO_API_URL}/pwa/user-training/exercise/done`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );
    
    //console.log('Exercise marked as done successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error marking exercise as done:', error);
    if (error.response && error.response.status === 401 && error.response.data.error === 'Invalid or missing API key') {
      logout();
    }
    throw error;
  }
};

const SessionView: React.FC = () => {
  const { planId, sessionId } = useParams();
  const nav = useNavigate();
  const { translate } = useLanguage();
  const { setLoading } = useLoadingOverlay();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const queryClient = useQueryClient();

  const userTrainingId = Number(planId);
  const { data: trainingDetail, isLoading: isTrainingLoading } = useTrainingSessions(userTrainingId);
  const { data: trainingsData } = useTrainings();

  useEffect(() => {
    setLoading(isTrainingLoading);
    if (!isTrainingLoading) {
      setShouldAnimate(true);
    }
  }, [isTrainingLoading, setLoading]);

  const currentSession = trainingDetail?.sessions?.find((s) => String(s.id) === String(sessionId));
  // Preload typed exercise details for each exercise to show parameters, descriptions, media
  const exercises = (currentSession?.exercises ?? []).map((ex: any) => {
    const meta = ex._typed as { typedExerciseId: number; type: string; trainingStepId: number } | undefined;
    return { ...ex, _meta: meta };
  });
  const sessionIndex = trainingDetail?.sessions?.findIndex((s) => s.id === sessionId) ?? 0;
  const sessionProgress = trainingDetail ? `${sessionIndex + 1}/${trainingDetail.sessions.length}` : '';
  const allCompleted = Boolean(currentSession?.isCompleted);
  const existingCompletionDate = currentSession?.finishedAt && currentSession?.createdAt ? new Date(currentSession.createdAt) : undefined;
  const currentTraining = trainingsData?.list?.find(t => t.id === userTrainingId);
  const isTrainingFinished = Boolean(currentTraining?.finishedAt);
  const isActiveSession = Boolean(!isTrainingFinished && trainingDetail?.activeStepId && String(trainingDetail.activeStepId) === String(sessionId));


  const accordionItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.2,
        ease: "easeOut"
      }
    })
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div 
      className="p-4 mt-4"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
    >
      <motion.div
        variants={fadeInFromTop}
        className="flex justify-center mt-3 mb-4"
      >
        <motion.button
          className={"uppercase text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:from-custom36s hover:to-custom36p focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-bold rounded-lg text-sm px-4 py-1.5 flex items-center justify-center"}
          onClick={() => nav('/trening', { replace: true, state: { fromSession: true } })}
          whileTap={{ scale: 0.95 }}
        >
          <IconArrowLeft className="mr-2" size={16} />
          {translate('goBack')}
        </motion.button>
      </motion.div>

      <motion.div 
        className="mt-4 mb-4"
        variants={fadeInFromTop}
      >
        <Flex direction={'column'} gap={'sm'}>
          <Paper px={'md'} py={'sm'} radius={'md'} style={{ 
            borderLeft: `5px solid ${isTrainingFinished ? colors.green : colors.custom36p}`,
            borderRight: `5px solid ${isTrainingFinished ? colors.green : colors.custom36p}`,
            borderRadius: '10px',
            }}>
            <Flex direction={'row'} justify={'space-between'} align={'center'}>
              <Flex direction="column" align="flex-start" gap={1}>
                <span className="font-semibold text-custom36s text-lg">{trainingDetail?.title}</span>
                {trainingDetail?.subtitle && (
                  <span className="text-sm text-gray-500">{trainingDetail.subtitle}</span>
                )}

              </Flex>

              <Flex direction={'column'} gap={1} align={'flex-end'} ml={8} mr={25}>
                <Text fw={700} c={colors.custom36p} size="xl">
                  {sessionProgress}
                </Text>
              </Flex>
            </Flex>
          </Paper>
        </Flex>
      </motion.div>

      <motion.div 
        variants={fadeInFromBottom}
      >
        <Paper p="md" shadow="xs" radius="md" >
          <Flex direction={'row'} justify={'space-between'} align={'center'} gap={'md'} mt={-6} mb={6}>

            <Flex direction={'row'} gap={'md'} ml={3}>
              <Text 
                fw={600} 
                c={colors.custom36p} 
                size='md'
              >
                {translate('session')} {sessionIndex + 1}
              </Text>

              <Flex align="center" gap={4}>
                <IconClock size={14} color={"gray"}/>
                <Text size="xs" c="dimmed" fw={500}>
                  {translate('sessionDurationShort', { duration: currentSession?.durationMinutes ?? 0 })}
                </Text>
              </Flex>
            </Flex>

            {allCompleted && (
            <Flex align={'center'}> 
              <Badge color="green" variant="light" size="sm" className="mx-auto">
                {existingCompletionDate ? translate('completionDate', { date: existingCompletionDate.toLocaleDateString('pl-PL', { day: '2-digit', month:'short', year:'numeric' }) }) : translate('completedBadge')}
              </Badge>
            </Flex>
            )}
            {!allCompleted && isActiveSession && (
              <Flex align={'center'}>
                <Badge color={colors.custom36p} variant="light" size="sm" className="mx-auto">
                  {translate('activeBadge')}
                </Badge>
              </Flex>
            )}
            
             
          </Flex>

          <Accordion 
            variant="separated" 
            radius="md"
          >
            {exercises.map((exercise, i) => (
              <motion.div
                key={`${exercise.name}-${i}`}
                custom={i}
                variants={accordionItemVariants}
              >
                <ExerciseAccordionItem 
                  exercise={exercise}
                  index={i}
                  isActive={Boolean(isActiveSession)}
                  isSessionFinished={Boolean(currentSession?.isCompleted)}
                  isTrainingFinished={isTrainingFinished}
                  onDone={async (params: Record<string, any>, exerciseType: string) => {
                    try {
                      setLoading(true);
                      await markExerciseAsDone(
                        userTrainingId,
                        exercise._meta.trainingStepId,
                        exercise._meta.exerciseId,
                        exercise._meta.typedExerciseId,
                        exerciseType,
                        params
                      );
                      // Refresh data after successful submission
                      await queryClient.invalidateQueries({ queryKey: ['training-sessions', userTrainingId] });
                      await queryClient.invalidateQueries({ queryKey: ['trainings'] });
                    } catch (error) {
                      console.error('Failed to mark exercise as done:', error);
                      alert('Błąd podczas zapisywania ćwiczenia. Spróbuj ponownie.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  openItem={null}
                  setOpenItem={() => {}}
                  translate={translate}
                />
              </motion.div>
            ))}
          </Accordion>
        </Paper>
        
        
      </motion.div>

      
    </motion.div>
  );
};

interface ExerciseAccordionItemProps {
  exercise: any;
  index: number;
  isActive: boolean;
  isSessionFinished?: boolean;
  isTrainingFinished?: boolean;
  onDone: (params: Record<string, any>, exerciseType: string) => Promise<void>;
  openItem: string | null;
  setOpenItem: (value: string | null) => void;
  translate: (key: string, values?: Record<string, string | number>) => string;
}

const ExerciseAccordionItem: React.FC<ExerciseAccordionItemProps> = ({
  exercise,
  index,
  isActive,
  isSessionFinished,
  isTrainingFinished,
  onDone,
  openItem,
  setOpenItem,
  translate
}) => {
  const meta = exercise._meta as { typedExerciseId: number; type: string; trainingStepId: number, exerciseId: number } | undefined;
  const detail = useSession(meta?.typedExerciseId || 0, meta?.type || '', meta?.trainingStepId || 0);
  const typed = detail.data;
  const isDataLoaded = !detail.isLoading && !!typed;
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const exerciseType = meta?.type || 'OTHER';

  // Extract real values from completed exercise
  const getRealValue = (metric: string): number | null => {
    if (!exercise.realValues || !exercise.done) return null;
    
    const realValues = exercise.realValues;
    
    // Extract values based on exercise type and metric
    if (exerciseType === 'MACHINE' && realValues.machineType) {
      const machineType = realValues.machineType;
      switch (metric) {
        case 'concentric': return machineType.concentric != null ? machineType.concentric : null;
        case 'eccentric': return machineType.eccentric != null ? machineType.eccentric : null;
        case 'reps': return machineType.reps != null ? machineType.reps : null;
        case 'delta': return machineType.delta != null ? machineType.delta : null;
        case 'speed': return machineType.rate != null ? machineType.rate : null;
        default: return null;
      }
    } else if (exerciseType === 'MACHINE_POWER_BASED' && realValues.machinePowerBasedType) {
      const machinePowerBasedType = realValues.machinePowerBasedType;
      switch (metric) {
        case 'w': return machinePowerBasedType.brakeW != null ? machinePowerBasedType.brakeW : null;
        case 'nm': return machinePowerBasedType.brakeNm != null ? machinePowerBasedType.brakeNm : null;
        case 'additionalTime': return machinePowerBasedType.timer != null ? machinePowerBasedType.timer : null;
        case 'mode': return machinePowerBasedType.mode != null ? machinePowerBasedType.mode : null;
        case 'speed': return machinePowerBasedType.rate != null ? machinePowerBasedType.rate : null;
        default: return null;
      }
    } else if (exerciseType === 'CARDIO' && realValues.cardioType) {
      const cardioType = realValues.cardioType;
      switch (metric) {
        case 'kilometry': return cardioType.km != null ? cardioType.km : null;
        case 'puls': return cardioType.pulse != null ? cardioType.pulse : null;
        case 'reps': return cardioType.reps != null ? cardioType.reps : null;
        case 'speed': return cardioType.rate != null ? cardioType.rate : null;
        default: return null;
      }
    } else if (exerciseType === 'STRENGTH' && realValues.strengthType) {
      const strengthType = realValues.strengthType;
      switch (metric) {
        case 'reps': return strengthType.reps != null ? strengthType.reps : null;
        case 'speed': return strengthType.rate != null ? strengthType.rate : null;
        default: return null;
      }
    } else if (exerciseType === 'OTHER' && realValues.otherType) {
      const otherType = realValues.otherType;
      switch (metric) {
        case 'puls': return otherType.pulse != null ? otherType.pulse : null;
        case 'reps': return otherType.reps != null ? otherType.reps : null;
        case 'speed': return otherType.rate != null ? otherType.rate : null;
        default: return null;
      }
    }
    
    return null;
  };

  // Build editable params from typed metrics (excluding time as it's not user-editable)
  const buildParams = React.useCallback((metrics: any) => {
    const entries: Array<{ key: string; label: string; value: number | string; type: 'number' | 'select' } > = [];
    const pushNum = (k: string, label: string) => {
      if (metrics[k]?.value != null) entries.push({ key: k, label, value: metrics[k].value, type: 'number' });
    };
    pushNum('concentric', 'Concentric (kg)');
    // Delta or eccentric
    const deltaValue = metrics['delta']?.value;
    if (deltaValue != null && deltaValue > 0) {
      entries.push({ key: 'delta', label: 'Delta (%)', value: deltaValue, type: 'number' });
    } else {
      pushNum('eccentric', 'Eccentric (kg)');
    }
    pushNum('reps', exerciseType === 'CARDIO' ? 'Okrążenia' : 'Powtórzenia');
    // pushNum('time', 'Czas (min)');
    //pushNum('wat', 'W (W)');
    pushNum('w', 'Obciążenie W (W)');
    pushNum('nm', 'Obciążenie Nm (Nm)');
    pushNum('additionalTime', 'Dodatkowy czas (s)');
    pushNum('kilometry', 'Kilometry (km)');
    pushNum('puls', 'Puls');
    const speed = metrics['speed'];
    if (speed) entries.push({ key: 'speed', label: 'Prędkość', value: speed, type: 'select' });
    return entries;
  }, [exerciseType]);

  const [params, setParams] = useState<Array<{ key: string; label: string; value: number | string; type: 'number' | 'select' }>>([]);

  // Update params when data loads
  React.useEffect(() => {
    if (isDataLoaded && typed?.exercises?.[0]?.metrics) {
      const newParams = buildParams(typed.exercises[0].metrics);
      setParams(newParams);
    }
  }, [isDataLoaded, typed, buildParams]);
  const getMetricIcons = (metric: string, exerciseType?: string) => {
    switch (metric) {
      case 'concentric':
      case 'eccentric':
        return <IconWeight size={18} />;
      case 'reps':
        return exerciseType === 'CARDIO' ? <IconReload size={18} /> : <IconBarbell size={18} />;
      case 'time':
        return <IconClock size={18} />;
      case 'wat':
      case 'w':
        return <IconGauge size={18} />;
      case 'nm':
        return <IconWeight size={18} />;
      case 'additionalTime':
        return <IconClockUp size={18} />;
      case 'mode':
        return <IconTreadmill size={18} />;
      case 'kilometry':
        return <IconBike size={18} />;
      case 'delta':
        return <IconTransfer size={18} />;
      case 'puls':
        return <IconHeartbeat size={18} />;
      case 'speed':
        return <IconRun size={18} />;
      default:
        return <IconBarbell size={18} />;
    }
  };
  
  const getMetricColor = (change: number) => {
    if (change > 0) return 'black';
    if (change < 0) return 'black';
    return colors.custom36p;
  };

  const getMetricName = (metric: string, exerciseType?: string) => {
    switch (metric) {
      case 'concentric':
        return translate('concentric');
      case 'eccentric':
        return translate('eccentric');
      case 'reps':
        return exerciseType === 'CARDIO' ? 'Okrążenia' : translate('repetitions');
      case 'time':
        return translate('duration');
      case 'wat':
        return translate('power');
      case 'w':
        return 'Obciążenie W';
      case 'nm':
        return 'Obciążenie Nm';
      case 'additionalTime':
        return 'Dodatkowy czas';
      case 'mode':
        return 'Tryb';
      case 'kilometry':
        return translate('distance');
      case 'delta':
        return translate('delta');
      case 'puls':
        return translate('pulse');
      case 'speed':
        return translate('speed');
      default:
        return metric;
    }
  };
  
  const getMetricLabel = (metric: string, value: number, change: number, exerciseType?: string, realValue?: number | null) => {
    let unit = '';
    const showDeltaInline = ['concentric', 'eccentric', 'delta', 'w', 'nm', 'wat', 'additionalTime'].includes(metric);
    switch (metric) {
      case 'concentric':
      case 'eccentric':
        unit = ' kg';
        break;
      case 'reps':
        unit = '';
        break;
      case 'time':
        unit = ' min';
        break;
      case 'wat':
        unit = ' W';
        break;
      case 'w':
        unit = ' W';
        break;
      case 'nm':
        unit = ' Nm';
        break;
      case 'additionalTime':
        unit = ' s';
        break;
      case 'mode':
        unit = '';
        break;
      case 'kilometry':
        unit = ' km';
        break;
      case 'delta':
        unit = ' %';
        break;
      case 'puls':
        unit = '';
        break;
    }

    const shouldShowBothValues = realValue !== null && typeof realValue === 'number' && realValue !== value && exercise.done;
    
    return (
      <Flex direction="column" align="center" className="w-28" >
        <Flex align="center" gap={6}>
          {getMetricIcons(metric, exerciseType)}
          <Text size="lg" fw={700} style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6}}>
            {showDeltaInline ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {value < 0 ? (
                  <IconArrowDown size={16} color="green" />
                ) : (
                  <IconArrowUp size={16} color="red" />
                )}
                <span style={{ color: getMetricColor(value < 0 ? -1 : 1), fontWeight: 700 }}>
                  {value < 0 ? '-' : '+'}{Math.abs(value)}{unit}
                  {shouldShowBothValues && (
                    <span style={{ color: colors.green, marginLeft: '4px' }}>
                      ({(realValue as number) < 0 ? '-' : '+'}{Math.abs(realValue as number)})
                    </span>
                  )}
                </span>
              </span>
            ) : (
              <span>
                {shouldShowBothValues ? (
                  <>
                    {value}{unit} <span style={{ color: colors.green }}>({realValue as number})</span>
                  </>
                ) : (
                  <>{realValue !== null && typeof realValue === 'number' && exercise.done ? realValue : value}{unit}</>
                )}
              </span>
            )}
          </Text>
        </Flex>
        <Text size="sm" c="dimmed" mt={1}>
          {getMetricName(metric, exerciseType)}
        </Text>
      </Flex>
    );
  };
  
  const getSpeedLabel = (speed: string, realSpeed?: number | null) => {
    const getSpeedText = (speedValue: string | number, abbreviated: boolean = false) => {
      // Handle both string format ('fast', 'normal', 'slow') and numeric format (0, 1, 2)
      let speedType: string;
      if (typeof speedValue === 'number') {
        speedType = speedValue === 0 ? 'slow' : speedValue === 1 ? 'normal' : 'fast';
      } else {
        speedType = speedValue;
      }
      
      if (abbreviated) {
        return speedType === 'fast' ? 'Szyb.' : speedType === 'slow' ? 'Woln.' : 'Norm.';
      } else {
        return speedType === 'fast' ? 'Szybko' : speedType === 'slow' ? 'Wolno' : 'Normalnie';
      }
    };

    const currentSpeedText = getSpeedText(speed);
    // Convert string speed to number for comparison
    const currentSpeedNum = speed === 'fast' ? 2 : speed === 'slow' ? 0 : 1;
    const shouldShowBothValues = realSpeed !== null && typeof realSpeed === 'number' && realSpeed !== currentSpeedNum && exercise.done;

    return (
      <Flex direction="column" align="center" className="w-28">
        <Flex align="center" gap={6}>
          {getMetricIcons('speed')}
          <Text size="lg" fw={700} style={{whiteSpace: 'nowrap'}}>
            {shouldShowBothValues ? (
              <>
                {getSpeedText(speed, true)} <span style={{ color: colors.green }}>({getSpeedText(realSpeed, true)})</span>
              </>
            ) : (
              <>{realSpeed !== null && typeof realSpeed === 'number' && exercise.done ? getSpeedText(realSpeed) : currentSpeedText}</>
            )}
          </Text>
        </Flex>
        <Text size="sm" c="dimmed" mt={1}>
          Prędkość
        </Text>
      </Flex>
    );
  };
  
  const getBgColor = () => {
    if (exercise.done) return `${colors.green}08`;
    if (exercise.skipped) return `${colors.gray}10`;
    return `${colors.custom36p}05`;
  };

  const getBorderColor = () => {
    if (exercise.done)    return `${colors.green}30`;
    if (exercise.skipped) return `${colors.gray}30`;
    return `${colors.custom36p}30`;
  };
  
  const [isAnimating] = useState(false);
  const [showSparkles] = useState(false);

  const buttonAnimationVariants = {
    initial: { 
      scale: 1,
      rotateY: 0,
      rotateX: 0
    },
    animate: { 
      scale: [1, 1.1, 0.5, 0], 
      rotateY: [0, 180, 360],
      rotateX: [0, 20, -20, 0],
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      }
    }
  };
  
  return (
    <Accordion.Item 
      value={`item-${index}`}
      style={{
        backgroundColor: getBgColor(),
        borderColor: getBorderColor(),
        borderWidth: '1px'
      }}
      mb={'xs'}
    >
      <Accordion.Control>
        <Flex align="center" justify="flex-start">
          <ThemeIcon 
            radius="xl" 
            size="md" 
            color={exercise.done ? 'green' : exercise.skipped ? 'gray' : `${colors.customGray}00`}
            variant={exercise.done || exercise.skipped ? 'filled' : 'light'}
            className="mr-3"
          >
            {exercise.done ? (
              <IconCircleCheck size={18} />
            ) : exercise.skipped ? (
              <IconCircleX size={18} />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-gray-400"></div>
            )}
          </ThemeIcon>
          <Text fw={600} c={colors.custom36s}>{typed?.exercises?.[0]?.name || exercise.name}</Text>
        </Flex>
      </Accordion.Control>
      <Accordion.Panel>
        {detail.isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader 
              size="md" 
              color={colors.custom36p} 
              type="bars" 
            />
          </div>
        ) : (
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {(typed?.exercises?.[0]?.metrics || exercise.metrics) && (
              <motion.div variants={fadeIn}>
                <Flex justify="center" gap="md" pb="md" mb={1} wrap="wrap">
                   {(typed?.exercises?.[0]?.metrics?.concentric || exercise.metrics?.concentric) && (
                     getMetricLabel('concentric', (typed?.exercises?.[0]?.metrics?.concentric?.value ?? exercise.metrics?.concentric?.value) as number, (typed?.exercises?.[0]?.metrics?.concentric?.change ?? exercise.metrics?.concentric?.change) as number, exerciseType, getRealValue('concentric'))
                   )}
                   {/* Show delta or eccentric */}
                   {(() => {
                     const deltaValue = typed?.exercises?.[0]?.metrics?.delta?.value ?? exercise.metrics?.delta?.value;
                     if (deltaValue != null && deltaValue > 0) {
                       return getMetricLabel('delta', deltaValue as number, (typed?.exercises?.[0]?.metrics?.delta?.change ?? exercise.metrics?.delta?.change) as number, exerciseType, getRealValue('delta'));
                     } else if (typed?.exercises?.[0]?.metrics?.eccentric || exercise.metrics?.eccentric) {
                       return getMetricLabel('eccentric', (typed?.exercises?.[0]?.metrics?.eccentric?.value ?? exercise.metrics?.eccentric?.value) as number, (typed?.exercises?.[0]?.metrics?.eccentric?.change ?? exercise.metrics?.eccentric?.change) as number, exerciseType, getRealValue('eccentric'));
                     }
                     return null;
                   })()}
                   {(typed?.exercises?.[0]?.metrics?.wat || exercise.metrics?.wat) && (
                     getMetricLabel('wat', (typed?.exercises?.[0]?.metrics?.wat?.value ?? exercise.metrics?.wat?.value) as number, (typed?.exercises?.[0]?.metrics?.wat?.change ?? exercise.metrics?.wat?.change) as number, exerciseType, getRealValue('wat'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.w || exercise.metrics?.w) && (
                     getMetricLabel('w', (typed?.exercises?.[0]?.metrics?.w?.value ?? exercise.metrics?.w?.value) as number, (typed?.exercises?.[0]?.metrics?.w?.change ?? exercise.metrics?.w?.change) as number, exerciseType, getRealValue('w'))
                   )}
                   {/* TO USE FOR ALFA MACHINES IN FUTURE */}
                   {/* {(typed?.exercises?.[0]?.metrics?.nm || exercise.metrics?.nm) && (
                     getMetricLabel('nm', (typed?.exercises?.[0]?.metrics?.nm?.value ?? exercise.metrics?.nm?.value) as number, (typed?.exercises?.[0]?.metrics?.nm?.change ?? exercise.metrics?.nm?.change) as number, exerciseType, getRealValue('nm'))
                   )} */}
                   {(typed?.exercises?.[0]?.metrics?.additionalTime || exercise.metrics?.additionalTime) && (
                     getMetricLabel('additionalTime', (typed?.exercises?.[0]?.metrics?.additionalTime?.value ?? exercise.metrics?.additionalTime?.value) as number, (typed?.exercises?.[0]?.metrics?.additionalTime?.change ?? exercise.metrics?.additionalTime?.change) as number, exerciseType, getRealValue('additionalTime'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.mode != null || exercise.metrics?.mode != null) && (
                     getMetricLabel('mode', (typed?.exercises?.[0]?.metrics?.mode?.value ?? exercise.metrics?.mode?.value) as number, (typed?.exercises?.[0]?.metrics?.mode?.change ?? exercise.metrics?.mode?.change) as number, exerciseType, getRealValue('mode'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.kilometry || exercise.metrics?.kilometry) && (
                     getMetricLabel('kilometry', (typed?.exercises?.[0]?.metrics?.kilometry?.value ?? exercise.metrics?.kilometry?.value) as number, (typed?.exercises?.[0]?.metrics?.kilometry?.change ?? exercise.metrics?.kilometry?.change) as number, exerciseType, getRealValue('kilometry'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.reps || exercise.metrics?.reps) && (
                     getMetricLabel('reps', (typed?.exercises?.[0]?.metrics?.reps?.value ?? exercise.metrics?.reps?.value) as number, (typed?.exercises?.[0]?.metrics?.reps?.change ?? exercise.metrics?.reps?.change) as number, exerciseType, getRealValue('reps'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.time || exercise.metrics?.time) && (
                     getMetricLabel('time', (typed?.exercises?.[0]?.metrics?.time?.value ?? exercise.metrics?.time?.value) as number, (typed?.exercises?.[0]?.metrics?.time?.change ?? exercise.metrics?.time?.change) as number, exerciseType, getRealValue('time'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.speed || exercise.metrics?.speed) && (
                     getSpeedLabel((typed?.exercises?.[0]?.metrics?.speed ?? exercise.metrics?.speed) as string, getRealValue('speed'))
                   )}
                   {(typed?.exercises?.[0]?.metrics?.puls || exercise.metrics?.puls) && (
                     getMetricLabel('puls', (typed?.exercises?.[0]?.metrics?.puls?.value ?? exercise.metrics?.puls?.value) as number, (typed?.exercises?.[0]?.metrics?.puls?.change ?? exercise.metrics?.puls?.change) as number, exerciseType, getRealValue('puls'))
                   )}
                   {/* Delta parameter is hidden for now - may be used in the future */}
                   {/* {(typed?.exercises?.[0]?.metrics?.delta || exercise.metrics?.delta) && (
                     getMetricLabel('delta', (typed?.exercises?.[0]?.metrics?.delta?.value ?? exercise.metrics?.delta?.value) as number, (typed?.exercises?.[0]?.metrics?.delta?.change ?? exercise.metrics?.delta?.change) as number, exerciseType, getRealValue('delta'))
                   )} */}
                </Flex>
              </motion.div>
            )}
            
            {exercise.done ? ( // If exercise is done, show green badge
              <motion.div 
                className="py-2 flex justify-center relative"
                variants={fadeIn}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Badge 
                    color={'green'} 
                    size="xl"
                  >
                    {translate('doneBadge')}
                  </Badge>
                </motion.div>
                {/* Sparkles for done exercises */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Sparkle color='#4CAF50' style={{ top: '40%', left: '22%' }} />
                </motion.div>
              </motion.div>
            ) : ( // If exercise is NOT done
              (!isTrainingFinished && (isActive || isSessionFinished)) ? ( // If training is active AND (session is active OR finished), show button
                <motion.div 
                  className="mb-4 relative"
                  variants={fadeIn}
                >
                  <motion.div
                    animate={isAnimating ? "animate" : "initial"}
                    variants={buttonAnimationVariants}
                    style={{ transformOrigin: "center", perspective: "1000px" }}
                  >
                    <button 
                      className={gradBtn} 
                      onClick={() => setModalOpen(true)}
                      disabled={isAnimating || !isDataLoaded}
                    >
                      <IconCircleCheck size={20} className="mr-2" /> {translate('doneButton')}
                    </button>
                  </motion.div>
                  {/* Sparkles for button animation */}
                  {showSparkles && (
                    <>
                      <Sparkle style={{ top: '30%', left: '20%' }} />
                      <Sparkle style={{ top: '10%', left: '50%' }} />
                      <Sparkle style={{ top: '30%', left: '80%' }} />
                      <Sparkle style={{ top: '70%', left: '70%' }} />
                      <Sparkle style={{ top: '70%', left: '30%' }} />
                      <Sparkle style={{ top: '50%', left: '10%' }} />
                      <Sparkle style={{ top: '50%', left: '90%' }} />
                    </>
                  )}
                </motion.div>
              ) : ( // If training is finished OR session is future, show gray badge
                <motion.div 
                  className="py-2 flex justify-center relative"
                  variants={fadeIn}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Badge 
                      color={'gray'} 
                      size="xl"
                    >
                      {translate('notDoneBadge')}
                    </Badge>
                  </motion.div>
                </motion.div>
              )
            )}
            
            <motion.div variants={fadeIn}>
              <Text size="md" fw={400} c={colors.custom36s} className="mt-6 text-center">
                {typed?.exercises?.[0]?.description || DESC[exercise.name]?.d}
              </Text>
            </motion.div>

            {(() => {
              const url = typed?.exercises?.[0]?.mediaUrl || DESC[exercise.name]?.url;
              if (!url) return null;
              return (
                <motion.div variants={fadeIn }>
                  {url.includes('youtube') ? (
                    <iframe
                      title="yt"
                      src={url.replace('watch?v=', 'embed/')}
                      className="w-full h-44 mt-4 rounded-lg shadow"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div className="flex justify-center mt-4">
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-custom36p font-bold flex items-center gap-1 underline"
                      >
                        {translate('externalMaterials')}
                        <IconExternalLink size={16} />
                      </a>
                    </div>
                  )}
                </motion.div>
              );
            })()}
          </motion.div>
        )}
      </Accordion.Panel>
      {/* Modal for editing exercise params */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-4 w-[90%] max-w-md shadow-lg">
            <Text fw={500} size="md" c={colors.custom36s} className="text-center">Wykonanie ćwiczenia</Text>
            <Text fw={700} size="lg" c={colors.custom36p} className="mb-5 text-center">{typed?.exercises?.[0]?.name || exercise.name}</Text>
            <div className="space-y-3 max-h-[60vh] overflow-auto mt-4 mb-5">
              {!isDataLoaded ? (
                <div className="flex justify-center items-center py-8">
                  <Loader size="md" />
                </div>
              ) : exerciseType === 'MACHINE' ? (
                <div className="text-center py-2">
                  <Text size="sm" c={colors.custom36s}>
                    Zatwierdź wykonanie ćwiczenia.
                  </Text>
                </div>
              ) : exerciseType === 'MACHINE_POWER_BASED' ? (
                <div className="text-center py-2">
                  <Text size="sm" c={colors.custom36s}>
                    Zatwierdź wykonanie ćwiczenia.
                  </Text>
                </div>
              ) : exerciseType === 'CONSULTATION' ? (
                <div className="text-center py-2">
                  <Text size="sm" c={colors.custom36s}>
                    Zatwierdź przeprowadzenie konsultacji.
                  </Text>
                </div>
              ) : params.length === 0 ? (
                <div className="text-center py-2">
                  <Text size="sm" c={colors.custom36s}>
                    Brak parametrów do edycji
                  </Text>
                </div>
              ) : (
                params.map((p, idx) => (
                <Flex key={p.key} justify="flex-end" align="center" gap="sm" className="w-72">
                  <Text size="sm" c={colors.custom36s} fw={500} className=" text-right pr-3">{p.label}</Text>
                  {p.type === 'number' ? (
                    <Flex align="center" gap={6} className="">
                      <button className="px-2 py-1 rounded bg-gray-100" onClick={() => setParams(prev => prev.map((x,i)=> i===idx ? { ...x, value: Math.max(0, Number(x.value) - 1) } : x))}>-</button>
                      <input 
                        value={p.value as number}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, '')
                          setParams(prev => prev.map((x,i)=> i===idx ? { ...x, value: v === '' ? 0 : Number(v) } : x))
                        }}
                        className="w-20 text-center border rounded px-2 py-1"
                        inputMode="numeric"
                      />
                      <button className="px-2 py-1 rounded bg-gray-100" onClick={() => setParams(prev => prev.map((x,i)=> i===idx ? { ...x, value: Number(x.value) + 1 } : x))}>+</button>
                    </Flex>
                  ) : (
                    <div className="w-1/2">
                      <Select
                        value={p.value as string}
                        onChange={(value) => setParams(prev => prev.map((x,i)=> i===idx ? { ...x, value: value || 'normal' } : x))}
                        data={[
                          { value: 'fast', label: 'Szybko' },
                          { value: 'normal', label: 'Normalnie' },
                          { value: 'slow', label: 'Wolno' }
                        ]}
                        size="xs"
                        styles={{
                          input: {
                            backgroundColor: 'white',
                            border: '1px solid #e1e5e9',
                            borderRadius: '6px',
                            fontSize: '14px',
                            padding: '4px 8px',
                            height: '32px'
                          }
                        }}
                        comboboxProps={{ withinPortal: false }}
                      />
                    </div>
                  )}
                </Flex>
                ))
              )}
            </div>
            <Flex justify="flex-end" gap="md" className="mt-4">
              <button 
                className="px-3 py-1 rounded text-white bg-gray-400 font-bold text-sm" 
                onClick={() => setModalOpen(false)}
                disabled={isSubmitting}
              >
                ANULUJ
              </button>
              <button 
                className="px-3 py-1 rounded text-white font-bold text-sm" 
                style={{ 
                  background: (!isDataLoaded || isSubmitting) ? '#ccc' : colors.custom36p,
                  opacity: isSubmitting ? 0.7 : 1
                }} 
                disabled={!isDataLoaded || isSubmitting}
                onClick={async () => { 
                  try {
                    setIsSubmitting(true);
                    
                    let finalParams: Record<string, any> = {};
                    
                    if (exerciseType === 'MACHINE') {
                      // For MACHINE exercises, use default values from typed data or defaults
                      finalParams = {
                        concentric: typed?.exercises?.[0]?.metrics?.concentric?.value || 0,
                        eccentric: typed?.exercises?.[0]?.metrics?.eccentric?.value || 0,
                        // delta: typed?.exercises?.[0]?.metrics?.delta?.value || 0, // Hidden for now
                        rate: typed?.exercises?.[0]?.metrics?.speed === 'slow' ? 0 : typed?.exercises?.[0]?.metrics?.speed === 'normal' ? 1 : 2,
                        reps: typed?.exercises?.[0]?.metrics?.reps?.value || 0
                      };
                    } else if (exerciseType === 'MACHINE_POWER_BASED') {
                      finalParams = {
                        brakeW: typed?.exercises?.[0]?.metrics?.w?.value || 0,
                        brakeNm: typed?.exercises?.[0]?.metrics?.nm?.value || 0,
                        timer: typed?.exercises?.[0]?.metrics?.additionalTime?.value || 0,
                        rate: typed?.exercises?.[0]?.metrics?.speed === 'slow' ? 0 : typed?.exercises?.[0]?.metrics?.speed === 'normal' ? 1 : 2
                      };
                    } else {
                      // For other exercise types, use edited parameters
                      finalParams = Object.fromEntries(params.map(p => {
                        if (p.key === 'speed') {
                          // Convert speed text to number: 'slow' -> 0, 'normal' -> 1, 'fast' -> 2
                          const speedValue = p.value === 'slow' ? 0 : p.value === 'normal' ? 1 : 2;
                          return ['rate', speedValue]; 
                        }
                        return [p.key, p.value];
                      }));
                    }
                    
                    console.log('WYKONANE finalParams', finalParams, 'exerciseType', exerciseType);
                    
                    // Call the onDone function
                    await onDone(finalParams, exerciseType);
                    setModalOpen(false);
                  } catch (error) {
                    console.error('Error in ZATWIERDŹ:', error);
                    // Error handling is done in the parent component
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                {isSubmitting ? 'ZAPISYWANIE...' : 'ZATWIERDŹ'}
              </button>
            </Flex>
          </div>
        </div>
      )}
    </Accordion.Item>
  );
};

export default SessionView;
